import { useTranslation } from 'react-i18next';
import type { ProjectArtifact } from '@/entities/project-artifact';
import { useDeleteProjectFile, useDetachArtifact } from '@/features/manage-project-artifacts';
import { notificationService } from '@/lib/services/notification.service';

interface UseLibraryDeleteOptions {
  projectId: string;
  onCleared: () => void;
}

/**
 * Centralises delete dispatch for the merged Library grid. The DTO funnels two
 * backing tables — attached artifacts and uploaded files — through one row
 * shape, so each row must route to the endpoint that actually owns its
 * lifecycle. Co-locating both mutations behind one entry point keeps the page
 * thin and makes the origin branch the single source of truth.
 */
export function useLibraryDelete(options: UseLibraryDeleteOptions) {
  const { t } = useTranslation('projects');
  const { projectId, onCleared } = options;
  const detachMutation = useDetachArtifact({ projectId });
  const deleteFileMutation = useDeleteProjectFile({ projectId });

  const confirmDelete = (artifact: ProjectArtifact) => {
    if (artifact.origin === 'upload') {
      deleteFileMutation.mutate(artifact.id, {
        onSuccess: () => {
          notificationService.showSuccess(t('projectDetail.useLibraryDelete.fileDeleted'));
          onCleared();
        },
        onError: () =>
          notificationService.showError(t('projectDetail.useLibraryDelete.couldNotDeleteFile')),
      });
      return;
    }

    detachMutation.mutate(artifact.id, {
      onSuccess: () => {
        notificationService.showSuccess(
          t('projectDetail.useLibraryDelete.artifactRemovedFromProject'),
        );
        onCleared();
      },
      onError: () =>
        notificationService.showError(
          t('projectDetail.useLibraryDelete.couldNotRemoveArtifactFrom'),
        ),
    });
  };

  return {
    confirmDelete,
    isPending: detachMutation.isPending || deleteFileMutation.isPending,
  };
}
